<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

import type { GameDateTime } from '../../common/services/GameTimeService';
import GameDetail from './components/GameDetail.vue';
import GameList from './components/GameList.vue';
import { runtimeUrls } from './runtimeUrls';
import type {
  AdminMessage,
  EntityTickUpdateData,
  GameUpdateData,
} from './types';

// Auth State
type AuthViewState = 'loading' | 'unauthenticated' | 'access-denied' | 'authenticated';
const authState = ref<AuthViewState>('loading');
const adminUser = ref<{ email: string; displayName: string | null } | null>(null);

// WebSocket configuration
const wsUrl = runtimeUrls.getAdminWebSocketUrl().href;

// UI State
const socket = ref<WebSocket | null>(null);
const connectionStatus = ref('Connecting...');
const connectionColor = ref('#888');
const games = ref<string[]>([]);
const currentGameId = ref<string | null>(null);
const gameData = ref<GameUpdateData | null>(null);

/** Per-tick entity update data (separate from game polling data). */
const entityTickData = ref<EntityTickUpdateData | null>(null);

// Reconnection logic
let reconnectDelay = 1000;
const maxReconnectDelay = 30000;
let reconnectTimer: number | null = null;

/**
 * Checks current authentication status.
 */
async function checkAuth() {
  try {
    const response = await fetch(runtimeUrls.getAdminAuthMeUrl(), {
      credentials: 'include',
    });
    if (response.ok) {
      adminUser.value = await response.json();
      authState.value = 'authenticated';
      connect();
    } else if (response.status === 403) {
      adminUser.value = await response.json();
      authState.value = 'access-denied';
    } else {
      authState.value = 'unauthenticated';
    }
  } catch (err) {
    console.error('Failed to check auth:', err);
    authState.value = 'unauthenticated';
  }
}

/**
 * Initiates Google OAuth login.
 */
function handleLogin() {
  window.location.href = runtimeUrls.getGoogleLoginUrl();
}

/**
 * Establishes a WebSocket connection to the server and handles lifecycle events.
 */
function connect() {
  if (socket.value) return;

  socket.value = new WebSocket(wsUrl);

  socket.value.onopen = () => {
    connectionStatus.value = 'Connected';
    connectionColor.value = '#4caf50';
    reconnectDelay = 1000; // Reset backoff on successful connection

    // Re-subscribe to relevant updates
    if (!currentGameId.value) {
      subscribeGames();
    } else {
      subscribeGame(currentGameId.value);
    }
  };

  socket.value.onclose = () => {
    connectionStatus.value = `Disconnected. Reconnecting in ${reconnectDelay / 1000}s...`;
    connectionColor.value = '#f44336';
    socket.value = null;

    // Exponential backoff for reconnection
    reconnectTimer = window.setTimeout(connect, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, maxReconnectDelay);
  };

  socket.value.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as AdminMessage;
      handleMessage(data);
    } catch (err) {
      console.error('Failed to parse WebSocket message:', err);
    }
  };
}

/**
 * Dispatches incoming WebSocket messages to state updates.
 */
function handleMessage(data: AdminMessage) {
  switch (data.type) {
    case 'GAMES_LIST':
      games.value = data.games;
      break;
    case 'GAME_UPDATE':
      gameData.value = data;
      break;
    case 'GAME_NOT_FOUND':
      console.warn('Game not found or ended:', data.gameId);
      showList();
      break;
    case 'ENTITY_TICK_UPDATE':
      entityTickData.value = data;
      break;
  }
}

function subscribeGames() {
  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify({ type: 'SUBSCRIBE_GAMES' }));
  }
}

function subscribeGame(gameId: string) {
  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify({ type: 'SUBSCRIBE_GAME', gameId }));
  }
}

function subscribeEntity(entityId: number) {
  if (socket.value && socket.value.readyState === WebSocket.OPEN && currentGameId.value) {
    socket.value.send(
      JSON.stringify({
        type: 'SUBSCRIBE_ENTITY',
        gameId: currentGameId.value,
        entityId,
      }),
    );
  }
}

function unsubscribeEntity() {
  entityTickData.value = null;
  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify({ type: 'UNSUBSCRIBE_ENTITY' }));
  }
}

function showList() {
  currentGameId.value = null;
  gameData.value = null;
  entityTickData.value = null;
  subscribeGames();
}

function selectGame(gameId: string) {
  currentGameId.value = gameId;
  entityTickData.value = null;
  subscribeGame(gameId);
}

function sendCommand(command: { type: string; gameId: string; time?: GameDateTime }) {
  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify(command));
  }
}

function handlePause() {
  if (currentGameId.value) {
    sendCommand({ type: 'PAUSE_GAME', gameId: currentGameId.value });
  }
}

function handleResume() {
  if (currentGameId.value) {
    sendCommand({ type: 'RESUME_GAME', gameId: currentGameId.value });
  }
}

function handleStep() {
  if (currentGameId.value) {
    sendCommand({ type: 'STEP_GAME', gameId: currentGameId.value });
  }
}

function handleSetGameTime(time: GameDateTime) {
  if (currentGameId.value) {
    sendCommand({ type: 'SET_GAME_TIME', gameId: currentGameId.value, time });
  }
}

onMounted(() => {
  checkAuth();
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.close();
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
  }
});
</script>

<template>
  <div class="container">
    <h1>Game Server Admin</h1>

    <!-- Loading State -->
    <div
      v-if="authState === 'loading'"
      class="loading"
    >
      Checking authentication...
    </div>

    <!-- Login View -->
    <div
      v-else-if="authState === 'unauthenticated'"
      class="login-card"
    >
      <p>Authentication required to access the admin site.</p>
      <button
        class="btn btn-primary"
        @click="handleLogin"
      >
        Sign in with Google
      </button>
    </div>

    <!-- Access Denied View -->
    <div
      v-else-if="authState === 'access-denied'"
      class="error-card"
    >
      <h2>Access Denied</h2>
      <p>Signed in as <strong>{{ adminUser?.email }}</strong>.</p>
      <p>This account does not have administrator privileges.</p>
      <button
        class="btn btn-secondary"
        @click="handleLogin"
      >
        Try another account
      </button>
    </div>

    <!-- Authenticated Dashboard -->
    <div v-else-if="authState === 'authenticated'">
      <div
        id="status"
        :style="{ color: connectionColor, marginBottom: '10px' }"
      >
        {{ connectionStatus }} ({{ adminUser?.email }})
      </div>

      <div v-if="!currentGameId">
        <GameList
          :games="games"
          @select="selectGame"
        />
      </div>

      <div v-else>
        <button
          class="btn back-btn"
          @click="showList"
        >
          ← Back to Games
        </button>
        <GameDetail
          v-if="gameData"
          :game-id="currentGameId"
          :data="gameData"
          :entity-tick-data="entityTickData"
          @pause="handlePause"
          @resume="handleResume"
          @step="handleStep"
          @set-game-time="handleSetGameTime"
          @subscribe-entity="subscribeEntity"
          @unsubscribe-entity="unsubscribeEntity"
        />
        <div v-else>
          Loading game details...
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-card, .error-card {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  margin: 50px auto;
}

.btn-primary {
  background-color: #4285f4;
  color: white;
  margin-top: 20px;
}

.btn-secondary {
  background-color: #888;
  color: white;
  margin-top: 20px;
}
</style>
